import { ExpandOutlined } from "@ant-design/icons";
import { ProviderNodeTitles } from "@p0security/shared/assessment/constants";
import { keyOf } from "@p0security/shared/graph/graph";
import { ConnectedNode, Node, NodeOf } from "@p0security/shared/graph/types";
import { assertNever } from "@p0security/shared/types/index";
import { Button } from "@p0security/ui";
import {
  NODE_BORDER_WIDTH,
  NodeBodyDiv,
  NodeContentDiv,
  NodeHighlight,
  NodeRenderProps,
} from "components/Assessment/components/GraphVisualization";
import { DiscoverVisualizationContext } from "components/Assessment/contexts/DiscoverVisualizationContext";
import { sortBy, sum } from "lodash";
import pluralize from "pluralize";
import { useCallback, useContext, useMemo } from "react";
import { colors } from "styles/colors";

import { Collapsed } from "./collapse";
import {
  DiscoverFlowSizes,
  NODE_HEADER_HEIGHT,
  convertFlowSize,
} from "./sizes";
import { DiscoverFlowRenderer, NodeDimensions } from "./types";

type CollapsibleNode<G extends object, K extends keyof G | "_collapsed"> = Node<
  G & Collapsed<G>,
  K
>;

type DiscoverArgs<P> = P & { open: Record<string, boolean> };

const CollapsibleNodeGapAndPadding = 8; // pixels

const CollapsibleNodeTitle = <G extends object>({
  node,
}: {
  node: CollapsibleNode<G, "_collapsed">;
}) => {
  const { open, provider, toggleOpen } = useContext(
    DiscoverVisualizationContext
  );
  const openKey = keyOf(node as any);

  const openNode = useCallback(
    () => toggleOpen(openKey),
    [openKey, toggleOpen]
  );

  const isOpen = open[openKey];

  const mode = node.data.type;
  const size = node.data.nodes.length;
  const type = node.data.nodes[0].type;

  const start =
    mode === "path" ? (
      <span>{node.data.title}</span>
    ) : mode === "like" ? (
      <span>
        {
          // TODO: restore abstraction here
          `${size} ${pluralize(
            String(
              (ProviderNodeTitles[provider] as Record<string, string>)[
                String(type)
              ] ?? type
            )
          )}`
        }
      </span>
    ) : (
      assertNever(mode)
    );

  return (
    <>
      {start}
      {isOpen && (
        <span style={{ paddingLeft: "4px" }}>
          (
          {
            // Rendered data are in a useMemo
            // eslint-disable-next-line react/jsx-no-bind
            <a type="link" onClick={openNode}>
              {isOpen ? "collapse" : "expand"}
            </a>
          }
          )
        </span>
      )}
    </>
  );
};

const CollapsibleNodeContent = <G extends object, P>({
  node,
  renderer,
  renderArgs,
}: {
  highlight: boolean;
  node: ConnectedNode<G & Collapsed<G>, "_collapsed">;
  renderer: DiscoverFlowRenderer<G, P>;
  renderArgs: DiscoverArgs<P>;
}) => {
  const { open, toggleOpen, instance } = useContext(
    DiscoverVisualizationContext
  );

  const openKey = keyOf(node as any);
  const mode = node.data.type;

  const anyHighlight = useMemo(
    () => node.data.nodes.some((n) => n.highlight),
    [node.data.nodes]
  );

  const inner = useMemo(() => {
    const source = node.data.nodes as (ConnectedNode<G, keyof G> &
      NodeHighlight)[];
    const data =
      mode === "path"
        ? // Maintain path order LTR
          source
        : mode === "like"
          ? // Order approximately by visual title
            sortBy(source, "key")
          : assertNever(mode);

    return data.map((n, ix) => {
      if (n.type === "_collapsed") return null;

      const { size } = renderer[n.type];
      const { height, width } = convertFlowSize(size).small;
      const contentSize = {
        height:
          mode === "path"
            ? DiscoverFlowSizes.twoLine.small.height
            : mode === "like"
              ? height
              : assertNever(mode),
        width,
      };
      return (
        <NodeBodyDiv
          {...renderer[n.type].style(n)}
          {...contentSize}
          key={ix}
          /* eslint-disable-line react/forbid-component-props */ style={
            contentSize
          }
          // Rendered data are in a useMemo
          // eslint-disable-next-line react/jsx-no-bind
          onClick={(e) => {
            if (n.type !== "_collapsed") {
              instance?.selectNode(n);
              // stopping the propagation here prevents the event handler
              // from being called on one of its parent nodes
              // (e.g. the _collapsed node)
              e.stopPropagation();
            }
          }}
        >
          <NodeContentDiv highlight={n.highlight}>
            {renderer[n.type].content(n as any, renderArgs)}
          </NodeContentDiv>
        </NodeBodyDiv>
      );
    });
  }, [instance, mode, node.data.nodes, renderArgs, renderer]);

  return node.type !== "_collapsed" ? null : open[openKey] ? (
    <div className="box-border flex flex-wrap justify-start gap-2">{inner}</div>
  ) : (
    <Button
      icon={<ExpandOutlined />}
      /* eslint-disable-line react/forbid-component-props */ style={{
        backgroundColor: anyHighlight ? colors.fill.highlight : undefined,
        height: "100%",
        opacity: "80%",
        width: "100%",
      }}
      // Rendered data are in a useMemo
      // eslint-disable-next-line react/jsx-no-bind
      onClick={() => toggleOpen(openKey)}
    >
      Expand
    </Button>
  );
};

export const collapsibleRender =
  (highlight: boolean) =>
  <G extends object, P>(
    render: DiscoverFlowRenderer<G, P>
  ): NodeRenderProps<G & Collapsed<G>, "_collapsed", DiscoverArgs<P>> => ({
    content: (node, extra) => (
      <CollapsibleNodeContent
        highlight={highlight}
        node={node as any}
        renderer={render}
        renderArgs={extra}
      />
    ),
    icon: "",
    size: collapsibleNodeSize(render),
    style: (node) => {
      const mode = node.data.type;
      const template = node.data.nodes[0];
      const inner = render[template.type].style(template as any);
      return {
        ...inner,
        backgroundColor:
          mode === "path"
            ? "hsl(0, 0%, 90%)"
            : mode === "like"
              ? `color-mix(in hsl, ${inner.backgroundColor} 65%, white)`
              : assertNever(mode),
      };
    },
    title: (node) => <CollapsibleNodeTitle node={node} />,
  });

const collapsibleNodeSize =
  <G extends object, P>(render: DiscoverFlowRenderer<G, P>) =>
  (
    node: CollapsibleNode<G, "_collapsed">,
    { open }: { open: Record<string, boolean> }
  ): NodeDimensions => {
    if (!open?.[keyOf(node as any)]) {
      return DiscoverFlowSizes.oneLine.large;
    }

    const mode = node.data.type;
    const subNodes = (node as Node<G & Collapsed<G>, "_collapsed">).data
      .nodes as NodeOf<G>[];

    // split array into chunks
    // 2-11 node is 2 columns, 12-47 is 3 columns, etc.
    const nColumn =
      mode === "path"
        ? subNodes.length
        : mode === "like"
          ? Math.max(Math.floor(Math.sqrt(subNodes.length / 3)), 2)
          : assertNever(mode);

    const columnWidths = new Map<number, number>();
    const rowHeights = new Map<number, number>();
    for (let i = 0; i < subNodes.length; i++) {
      const col = i % nColumn;
      const row = Math.floor(i / nColumn);
      const { size } = render[subNodes[i].type];
      const spec = convertFlowSize(size).small;
      columnWidths.set(col, Math.max(columnWidths.get(col) ?? 0, spec.width));
      rowHeights.set(row, Math.max(rowHeights.get(row) ?? 0, spec.height));
    }

    // calculate the total size occupied by an expanded collapsible node,
    // including padding and gap
    const width = sum([...columnWidths.values()]);
    const height = sum([...rowHeights.values()]);
    return {
      width:
        width +
        CollapsibleNodeGapAndPadding * (columnWidths.size + 1) +
        2 * NODE_BORDER_WIDTH,
      height:
        height +
        CollapsibleNodeGapAndPadding * (rowHeights.size + 2) +
        2 * NODE_BORDER_WIDTH +
        NODE_HEADER_HEIGHT,
    };
  };
